/**
 * jsondiffpatch設定とユーティリティ関数
 */

import * as jsondiffpatch from 'jsondiffpatch';
import { TRANSIENT_MARKER } from '../../../types/serialization.types';
import type { SimpleDiff } from './types';

// jsondiffpatchのデルタ型
export type Delta = jsondiffpatch.Delta;

/**
 * jsondiffpatchインスタンスを作成
 *
 * 設定:
 * - objectHash: オブジェクトのid属性でマッチング
 * - textDiff: 無効化（文字列は全置換）
 * - arrays.detectMove: 配列要素の移動を検出
 * - propertyFilter: __transient__プロパティを除外
 */
export function createDiffPatch(): jsondiffpatch.DiffPatcher {
  return jsondiffpatch.create({
    objectHash: (obj: unknown, index?: number) => {
      if (obj && typeof obj === 'object') {
        const record = obj as Record<string, unknown>;
        if ('id' in record && typeof record.id === 'string') {
          return record.id;
        }
      }
      return `$$index:${index}`;
    },
    // テキストdiffを無効化（文字列は全置換として扱う）
    // textDiffオプションを指定しないことで無効化
    arrays: {
      detectMove: true,
      includeValueOnMove: false,
    },
    propertyFilter: (name: string) => {
      // transientマーカーのプロパティを除外
      return name !== TRANSIENT_MARKER;
    },
  });
}

// シングルトンインスタンス
let diffPatchInstance: jsondiffpatch.DiffPatcher | null = null;

/**
 * シングルトンのdiffPatchインスタンスを取得
 */
export function getDiffPatch(): jsondiffpatch.DiffPatcher {
  if (!diffPatchInstance) {
    diffPatchInstance = createDiffPatch();
  }
  return diffPatchInstance;
}

/**
 * 2つのオブジェクトの差分を計算
 */
export function diff<T>(original: T, modified: T): Delta | undefined {
  return getDiffPatch().diff(original, modified);
}

/**
 * 差分があるかどうかを判定
 */
export function hasChanges<T>(original: T, modified: T): boolean {
  const delta = diff(original, modified);
  return delta !== undefined;
}

/**
 * 簡易差分を取得（変更有無と変更パスのリスト）
 */
export function getSimpleDiff<T>(original: T, modified: T): SimpleDiff {
  const delta = diff(original, modified);

  if (delta === undefined) {
    return { hasDifferences: false };
  }

  const changedPaths = extractChangedPaths(delta);
  return {
    hasDifferences: true,
    changedPaths,
  };
}

/**
 * デルタから変更があったパスのリストを抽出
 */
export function extractChangedPaths(delta: Delta, prefix: string = ''): string[] {
  const paths: string[] = [];

  if (!delta || typeof delta !== 'object') {
    return paths;
  }

  for (const [key, value] of Object.entries(delta)) {
    // jsondiffpatchの特殊キーをスキップ
    if (key === '_t') continue;

    const currentPath = prefix ? `${prefix}.${key}` : key;

    if (Array.isArray(value)) {
      // 配列の場合は変更があったことを示す
      // [oldValue, newValue] - 変更
      // [newValue] - 追加
      // [oldValue, 0, 0] - 削除
      paths.push(currentPath);
    } else if (typeof value === 'object' && value !== null) {
      // ネストしたオブジェクトの場合は再帰的に処理
      const nestedPaths = extractChangedPaths(value as Delta, currentPath);
      paths.push(...nestedPaths);
    }
  }

  return paths;
}

/**
 * 配列の追加/削除/変更を分析
 *
 * jsondiffpatchの配列デルタ形式:
 * - "_t": "a" - 配列であることを示す
 * - "_N": [value, 0, 0] - インデックスNの要素が削除
 * - "N": [value] - インデックスNの要素が追加
 * - "N": [oldValue, newValue] - インデックスNの要素が変更
 * - "_N": ['', destinationIndex, 3] - インデックスNの要素が移動
 */
export interface ArrayDiffResult<T> {
  added: { index: number; value: T }[];
  removed: { index: number; value: T }[];
  modified: { index: number; oldValue: T; newValue: T }[];
  moved: { fromIndex: number; toIndex: number }[];
}

/**
 * 配列デルタを解析して追加/削除/変更を取得
 */
export function parseArrayDelta<T>(delta: Delta | undefined): ArrayDiffResult<T> {
  const result: ArrayDiffResult<T> = {
    added: [],
    removed: [],
    modified: [],
    moved: [],
  };

  if (!delta || typeof delta !== 'object') {
    return result;
  }

  const deltaObj = delta as Record<string, unknown>;

  // 配列デルタでない場合
  if (deltaObj._t !== 'a') {
    return result;
  }

  for (const [key, value] of Object.entries(deltaObj)) {
    if (key === '_t') continue;

    if (!Array.isArray(value)) continue;

    // 削除または移動（キーが_で始まる）
    if (key.startsWith('_')) {
      const index = parseInt(key.slice(1), 10);
      if (value.length === 3 && value[2] === 0) {
        // 削除: [value, 0, 0]
        result.removed.push({ index, value: value[0] as T });
      } else if (value.length === 3 && value[2] === 3) {
        // 移動: ['', destinationIndex, 3]
        result.moved.push({ fromIndex: index, toIndex: value[1] as number });
      }
    } else {
      const index = parseInt(key, 10);
      if (value.length === 1) {
        // 追加: [value]
        result.added.push({ index, value: value[0] as T });
      } else if (value.length === 2) {
        // 変更: [oldValue, newValue]
        result.modified.push({ index, oldValue: value[0] as T, newValue: value[1] as T });
      }
    }
  }

  return result;
}

/**
 * Recordの追加/削除/変更を分析
 */
export interface RecordDiffResult<K extends string> {
  added: K[];
  removed: K[];
  modified: K[];
}

/**
 * Record型のデルタを解析して追加/削除/変更されたキーを取得
 */
export function parseRecordDelta<K extends string>(
  delta: Delta | undefined,
  originalKeys: K[],
  modifiedKeys: K[]
): RecordDiffResult<K> {
  const result: RecordDiffResult<K> = {
    added: [],
    removed: [],
    modified: [],
  };

  if (!delta || typeof delta !== 'object') {
    return result;
  }

  const originalSet = new Set(originalKeys);
  const modifiedSet = new Set(modifiedKeys);

  // 追加: modifiedにあってoriginalにない
  for (const key of modifiedKeys) {
    if (!originalSet.has(key)) {
      result.added.push(key);
    }
  }

  // 削除: originalにあってmodifiedにない
  for (const key of originalKeys) {
    if (!modifiedSet.has(key)) {
      result.removed.push(key);
    }
  }

  // 変更: 両方にあってデルタに含まれる
  const deltaObj = delta as Record<string, unknown>;
  for (const key of originalKeys) {
    if (modifiedSet.has(key) && key in deltaObj) {
      const value = deltaObj[key];
      // 追加/削除でなく変更の場合
      if (!Array.isArray(value) || value.length === 2) {
        result.modified.push(key);
      }
    }
  }

  return result;
}
